"use client";

// [ERP-POLLING-LOAD-REDUCTION-SAFE-DEV-20260707-035C]
// visibility 기반 폴링 유틸. 서버 부하의 대부분을 차지하는 채팅/알림 자동 polling 을
// 다음 규칙으로 완화한다:
//   1. 탭이 visible 일 때만 interval 로 반복 호출(주기 activeMs).
//   2. 탭이 hidden(백그라운드)이면 interval 중지 — 백그라운드 탭 요청 제거.
//   3. hidden → visible 복귀 시 즉시 1회 fetch 후 interval 재개(밀린 느낌 최소화).
//   4. requireToken=true(기본)면 access_token 없을 때 폴링 중지(로그아웃/만료).
//   5. in-flight 가드로 느린 응답 시 중복 호출 방지.
//   6. cleanup 에서 interval + visibilitychange listener 확실히 해제(중복 생성 방지).
// backend API/스키마 변경 없음. 기존 fetch 콜백을 그대로 감싸기만 한다.

import { useEffect, useRef } from "react";

interface Options {
  /** visible 상태 폴링 주기(ms). */
  activeMs: number;
  /** true(기본)면 localStorage access_token 없을 때 폴링 중지. */
  requireToken?: boolean;
  /** mount 시 즉시 1회 실행할지(기본 true). */
  immediate?: boolean;
  /** 폴링 활성 여부. false 면 전부 정지(예: authed=false). 기본 true. */
  enabled?: boolean;
}

/**
 * fn 을 visibility 인지 방식으로 주기 실행한다.
 * fn 은 매 호출 최신 참조가 쓰이도록 내부에서 ref mirror 한다(effect 재생성 없음).
 */
export function useVisibilityPolling(
  fn: () => void | Promise<void>,
  { activeMs, requireToken = true, immediate = true, enabled = true }: Options,
): void {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | null = null;
    let inFlight = false;
    let stopped = false;

    const hasToken = () =>
      !requireToken ||
      (typeof window !== "undefined" && !!localStorage.getItem("access_token"));

    const runOnce = async () => {
      if (inFlight || stopped) return;
      if (typeof document !== "undefined" && document.hidden) return;
      if (!hasToken()) return;
      inFlight = true;
      try {
        await fnRef.current();
      } catch {
        /* 개별 콜백에서 처리 — 여기선 무시 */
      } finally {
        inFlight = false;
      }
    };

    const startTimer = () => {
      if (timer) return;
      timer = setInterval(runOnce, activeMs);
    };
    const stopTimer = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) {
        // 백그라운드 → interval 중지(요청 제거)
        stopTimer();
      } else {
        // 복귀 → 즉시 1회 fetch 후 interval 재개
        void runOnce();
        startTimer();
      }
    };

    // 초기 시작: visible 이면 즉시 1회 + interval, hidden 이면 대기
    if (typeof document === "undefined" || !document.hidden) {
      if (immediate) void runOnce();
      startTimer();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      stopped = true;
      stopTimer();
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // activeMs/requireToken/immediate/enabled 변경 시에만 재구성.
  }, [activeMs, requireToken, immediate, enabled]);
}
